import { AuditLink } from '@optools/components/audit-link';
import { TeamSpot, TeamSpotProps } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Card, CardDescription } from '@optools/components/ui/card';
import { Separator } from '@optools/components/ui/separator';
import { cn } from '@optools/components/ui/utils';
import { BtcGameBroadcast } from '@optools/services/operator-gateway/btc/types';
import { MediaState, MediaType } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatPregameTime } from '@optools/utils/datetime';
import { formatEnumValue } from '@optools/utils/format';
import { Link } from '@tanstack/react-router';
import { capitalize } from 'es-toolkit';
import {
  Activity,
  Archive,
  Circle,
  CircleDot,
  Database,
  Earth,
  Lock,
  RadioTower,
  Speaker,
  Video,
} from 'lucide-react';
import { ReactNode, useId } from 'react';

const getMediaStateBadge = (mediaState: MediaState): ReactNode => {
  const formattedState = capitalize(mediaState);
  switch (mediaState) {
    case MediaState.ARCHIVE: {
      return (
        <Badge variant="secondary">
          <Archive />
          {formattedState}
        </Badge>
      );
    }
    case MediaState.OFF: {
      return (
        <Badge variant="outline">
          <Circle />
          {formattedState}
        </Badge>
      );
    }
    case MediaState.ON: {
      return (
        <Badge variant="default">
          <CircleDot />
          {formattedState}
        </Badge>
      );
    }
  }
};

export interface BroadcastCardProps {
  broadcast: BtcGameBroadcast;
  date: string;
  gamePk: number;
  team: TeamSpotProps['team'];
}

export function BroadcastCard({ broadcast, date, gamePk, team }: BroadcastCardProps) {
  const id = useId();
  const {
    availability,
    availableForStreaming,
    callSign,
    colorSpace,
    feedType,
    freeGameStatuses,
    language,
    mediaId,
    mediaState,
    mediaType,
    mvpdRequired,
    name,
    postGameShow,
    preGameShow,
    videoResolution,
  } = broadcast;
  return (
    <Card
      className="relative isolate flex h-full flex-col p-4 transition-colors has-[>a:hover]:bg-accent/70"
      data-testid={`broadcast-card-${mediaId}`}>
      <Link
        aria-label={`${broadcast.callSign} broadcast detail`}
        className="absolute inset-0"
        from="/"
        params={{ gamePk, mediaId: broadcast.mediaId }}
        search={{ date }}
        to="/btc-tools/schedule/$gamePk/broadcasts/$mediaId"
      />
      <header className="mb-6">
        <div className="mb-4 flex items-start justify-between">
          <div className="basis-2/3">
            <h1 className="mb-1 flex items-center gap-1 font-semibold">
              <RadioTower className="size-4" />
              {callSign}
            </h1>
            <p className="text-xs text-primary/70">{name}</p>
          </div>

          <div className="flex items-center gap-2">
            <TeamSpot showAbbreviation={false} size={24} team={team} />
            <span className="text-sm font-semibold">{feedType}</span>
          </div>
        </div>

        <div className="flex justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>
              {mediaType === MediaType.VIDEO ? <Video /> : <Speaker />} {mediaType}
            </Badge>

            {videoResolution && <Badge>{videoResolution}</Badge>}
            {colorSpace && <Badge>{colorSpace}</Badge>}
          </div>
        </div>
      </header>

      {mvpdRequired && (
        <Badge className="absolute top-4 left-1/2 -translate-x-1/2" variant="secondary">
          <Lock />
          MVPD
        </Badge>
      )}

      <CardDescription className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 id={`${id}-available-for-streaming`}>Available for Streaming</h2>
          <Badge
            aria-labelledby={`${id}-available-for-streaming`}
            className={cn(
              'gap-2 bg-transparent',
              availableForStreaming
                ? 'border-green-500 text-green-500'
                : 'border-red-500 text-red-500',
            )}>
            <span
              className={cn(
                'size-2 rounded-full',
                availableForStreaming ? 'bg-green-500' : 'bg-red-500',
              )}
            />
            {availableForStreaming ? 'Yes' : 'No'}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <h2 id={`${id}-availability`}>Availability</h2>
          <Badge aria-labelledby={`${id}-availability`} variant="secondary">
            {formatEnumValue(availability)}
          </Badge>
        </div>
      </CardDescription>

      <Separator className="my-4" />

      <dl className="py-2">
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div>
            <dt
              className="mb-2 flex items-center gap-1 text-sm whitespace-nowrap text-primary/80"
              id={`${id}-media-state`}>
              <Activity className="size-4 shrink-0" />
              Media State
            </dt>
            <dd aria-labelledby={`${id}-media-state`}>{getMediaStateBadge(mediaState)}</dd>
          </div>

          <div>
            <dt
              className="mb-2 flex items-center gap-1 text-sm whitespace-nowrap text-primary/80"
              id={`${id}-language`}>
              <Earth className="size-4 shrink-0" />
              Language
            </dt>
            <dd aria-labelledby={`${id}-language`} className="text-sm font-semibold uppercase">
              {language}
            </dd>
          </div>
        </div>

        <div>
          <dt
            className="mb-2 flex items-center gap-1 text-sm whitespace-nowrap text-primary/80"
            id={`${id}-mediaId`}>
            <Database className="size-4 shrink-0" />
            Media ID
          </dt>
          <dd
            aria-labelledby={`${id}-mediaId`}
            className="relative z-10 inline-block rounded-sm bg-accent p-1 font-mono text-xs">
            <AuditLink tag="mediaId" value={mediaId} />
          </dd>
        </div>
      </dl>

      {freeGameStatuses.length > 0 && (
        <>
          <Separator className="my-4" />
          <div>
            <h3 className="mb-2 text-sm whitespace-nowrap text-primary/80">Free Game Statuses</h3>
            <ul className="flex flex-wrap items-center gap-2">
              {freeGameStatuses
                .toSorted((a, b) => (a.status && !b.status ? -1 : 1))
                .map(({ freeGameType, status }) => (
                  <li key={freeGameType}>
                    <Badge
                      className={cn(status && 'border-green-500')}
                      variant={status ? 'secondary' : 'outline'}>
                      {status ? <span className="size-2 rounded-full bg-green-500" /> : <Circle />}
                      {formatEnumValue(freeGameType)}
                    </Badge>
                  </li>
                ))}
            </ul>
          </div>
        </>
      )}

      {(!!preGameShow || postGameShow) && (
        <>
          <Separator className="my-4" />
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt
                className="mb-2 text-sm whitespace-nowrap text-primary/80"
                id={`${id}-pre-game-show`}>
                Pre Game Show
              </dt>
              <dd aria-labelledby={`${id}-pre-game-show`} className="text-sm font-semibold">
                {preGameShow ? formatPregameTime(preGameShow) : 'No'}
              </dd>
            </div>

            <div>
              <dt
                className="mb-2 text-sm whitespace-nowrap text-primary/80"
                id={`${id}-post-game-show`}>
                Post Game Show
              </dt>
              <dd aria-labelledby={`${id}-post-game-show`} className="text-sm font-semibold">
                {postGameShow ? 'Yes' : 'No'}
              </dd>
            </div>
          </dl>
        </>
      )}
    </Card>
  );
}
